"use client";

import { useEffect, useState } from "react";
import { Notice, TextField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CountTable,
  MetricResultCard,
  type MetricView,
} from "@/components/tools/reference-tools/journal-metric-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { parseCount } from "@/lib/tool-kit/reference-tools/h-index";
import {
  citationRatio,
  windowYears,
} from "@/lib/tool-kit/reference-tools/journal-metrics";

/** English strings. A translated content file overrides these through `ui`. */
export const IMPACT_FACTOR_CALCULATOR_STRINGS = {
  yearLabel: "Metric year (the JCR year, or the CiteScore year)",
  yearPlaceholder: "2024",
  example: "Fill in example numbers",
  clear: "Clear",
  exampleNote: "The example numbers are illustrative, not a real journal's.",
  jifHeading: "Journal Impact Factor and five-year JIF",
  jifIntro:
    "For each year, enter the citations the journal's items from that year received in {year}, and the number of citable items (articles and reviews) it published that year.",
  citeScoreHeading: "CiteScore",
  citeScoreIntro:
    "For each year, enter the citations received in {first}–{year} by documents published that year, and the number of those documents (articles, reviews, conference papers, book chapters and data papers).",
  publishedIn: "Published in",
  jifCitations: "Citations in {year}",
  jifItems: "Citable items",
  csCitations: "Citations {first}–{year}",
  csDocuments: "Documents",
  jifCitationsLabel: "Citations in {year} to items published in {published}",
  jifItemsLabel: "Citable items published in {published}",
  csCitationsLabel:
    "Citations in {first}–{year} to documents published in {published}",
  csDocumentsLabel: "Documents published in {published}",
  jifWindow: "two-year JIF",
  fiveYearWindow: "five-year JIF only",
  enterYear: "Enter the metric year to set up the tables.",
  resultsHeading: "Results",
  jif: "Journal Impact Factor {year}",
  fiveYear: "Five-year Journal Impact Factor {year}",
  citeScore: "CiteScore {year}",
  jifMissing:
    "Fill in both rows marked two-year JIF to see the Journal Impact Factor.",
  fiveYearMissing: "Fill in all five rows to see the five-year JIF.",
  citeScoreMissing: "Fill in all four rows to see CiteScore.",
  working: "{numerator} ÷ {denominator} = {value}",
  sums: "({parts})",
  plus: " + ",
  exact: "Exact: {value}. JCR and Scopus publish one decimal place.",
  officialNote:
    "Official values come only from Clarivate's Journal Citation Reports (JIF) and Elsevier's Scopus (CiteScore), counted from their own indexes. This calculator reproduces the arithmetic on numbers you supply.",
  invalidCount: "Use whole numbers, such as 1204 or 1,204.",
} as const;

export type ImpactStrings = typeof IMPACT_FACTOR_CALCULATOR_STRINGS;

/** Illustrative counts: citations and items for Y−1 … Y−5, then CiteScore's Y−3 … Y. */
const EXAMPLE = {
  jifCitations: ["1204", "1563", "1320", "1102", "938"],
  jifItems: ["310", "296", "285", "270", "262"],
  csCitations: ["1890", "1744", "1232", "286"],
  csDocuments: ["301", "318", "335", "348"],
};

const EMPTY = {
  jifCitations: ["", "", "", "", ""],
  jifItems: ["", "", "", "", ""],
  csCitations: ["", "", "", ""],
  csDocuments: ["", "", "", ""],
};

type Counts = typeof EMPTY;

function values(raw: readonly string[]): (number | null)[] {
  return raw.map((text) => (text.trim() === "" ? null : parseCount(text)));
}

function view(
  citations: (number | null)[],
  items: (number | null)[]
): MetricView | null {
  const ratio = citationRatio(citations, items);
  if (!ratio) {
    return null;
  }
  return {
    ...ratio,
    citationParts: citations as number[],
    itemParts: items as number[],
  };
}

export default function ImpactFactorCalculatorWidget() {
  const strings = useToolUi(IMPACT_FACTOR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [yearText, setYearText] = useState("");
  const [counts, setCounts] = useState<Counts>(EMPTY);

  useEffect(() => {
    setYearText(String(new Date().getFullYear() - 1));
  }, []);

  const year = parseIntegerInput(yearText);
  const set = (key: keyof Counts, index: number, value: string) =>
    setCounts((current) => ({
      ...current,
      [key]: current[key].map((old, at) => (at === index ? value : old)),
    }));

  const jifCitations = values(counts.jifCitations);
  const jifItems = values(counts.jifItems);
  const jif = view(jifCitations.slice(0, 2), jifItems.slice(0, 2));
  const fiveYear = view(jifCitations, jifItems);
  const citeScore = view(
    values(counts.csCitations),
    values(counts.csDocuments)
  );
  const invalid = Object.values(counts)
    .flat()
    .some((text) => text.trim() !== "" && parseCount(text) === null);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-64">
          <TextField
            id="ifc-year"
            label={strings.yearLabel}
            numeric
            onChange={setYearText}
            placeholder={strings.yearPlaceholder}
            value={yearText}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setCounts(EXAMPLE)}
          type="button"
        >
          {strings.example}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setCounts(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {year === null ? (
        <Notice>{strings.enterYear}</Notice>
      ) : (
        <>
          <CountTable
            citations={counts.jifCitations}
            heading={strings.jifHeading}
            idPrefix="ifc-jif"
            intro={t(strings.jifIntro, { year })}
            items={counts.jifItems}
            kind="jif"
            metricYear={year}
            onCitations={(index, value) => set("jifCitations", index, value)}
            onItems={(index, value) => set("jifItems", index, value)}
            strings={strings}
            years={windowYears(year, 5, false)}
          />
          <CountTable
            citations={counts.csCitations}
            heading={strings.citeScoreHeading}
            idPrefix="ifc-cs"
            intro={t(strings.citeScoreIntro, { first: year - 3, year })}
            items={counts.csDocuments}
            kind="citeScore"
            metricYear={year}
            onCitations={(index, value) => set("csCitations", index, value)}
            onItems={(index, value) => set("csDocuments", index, value)}
            strings={strings}
            years={windowYears(year, 4, true).reverse()}
          />
          {counts === EXAMPLE ? (
            <p className={HINT}>{strings.exampleNote}</p>
          ) : null}
          {invalid ? <Notice>{strings.invalidCount}</Notice> : null}
          <div aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            <MetricResultCard
              fmt={fmt}
              missing={strings.jifMissing}
              strings={strings}
              title={t(strings.jif, { year })}
              view={jif}
            />
            <MetricResultCard
              fmt={fmt}
              missing={strings.fiveYearMissing}
              strings={strings}
              title={t(strings.fiveYear, { year })}
              view={fiveYear}
            />
            <MetricResultCard
              fmt={fmt}
              missing={strings.citeScoreMissing}
              strings={strings}
              title={t(strings.citeScore, { year })}
              view={citeScore}
            />
          </div>
        </>
      )}
      <p className={HINT}>{strings.officialNote}</p>
    </section>
  );
}
